import React, { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import { useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector } from "react-redux";
import useThemeColor from "../../hooks/useThemeColor";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import swal from "sweetalert";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import {
  SortOrderDropDownSelection,
} from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import "./../order/ManageOrderFilters.css";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";

const ManageBatch = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const navigate = useNavigate();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const isChildBranch = useIsChildBranch();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [sortBy, setSortBy] = useState({ value: "CreatedDate", label: "Created Date" });
  const [sortOrder, setSortOrder] = useState({ value: "DESC", label: "DESC" });
  const [batchStatus, setBatchStatus] = useState(null);
  const [isCompleted, setIsCompleted] = useState(null);
  const [originalBatchesCount, setOriginalBatchesCount] = useState(0);
  const [menuItems, setMenuItems] = useState([]);

  const sortByOptions = [
    { value: "CreatedDate", label: "Created Date" },
    { value: "BatchNumber", label: "Batch Number" },
    { value: "BatchStatus", label: "Status" },
    { value: "Quantity", label: "Quantity" },
  ];

  const batchStatusOptions = [
    { value: null, label: "All Status" },
    { value: "Draft", label: "Draft" },
    { value: "InProgress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
  ];

  const isCompletedOptions = [
    { value: null, label: "All" },
    { value: true, label: "Completed" },
    { value: false, label: "Not Completed" },
  ];

  // Fetch menu items for dropdown
  const fetchMenuItems = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_ONLY_PRODUCTS, {
        params: { CompID },
      });
      if (response.data && Array.isArray(response.data)) {
        const items = response.data.map((item) => ({
          value: item.menuItem_ID,
          label: item.menuItem_Name || `Item #${item.menuItem_ID}`,
        }));
        setMenuItems(items);
      }
    } catch (error) {
      console.error("Error fetching menu items:", error);
    }
  }, [CompID]);

  useEffect(() => {
    if (CompID) {
      fetchMenuItems();
    }
  }, [CompID, fetchMenuItems]);

  // Fetch batches data
  const fetchBatches = useCallback(async () => {
    if (!CompID) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_RECIPE_BATCHES, {
        params: {
          CompID,
          Search: search || "",
          PageSize: pageSize.value,
          PageNumber: pageNumber,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          BatchStatus: batchStatus?.value || null,
          IsCompleted: isCompleted?.value !== undefined ? isCompleted.value : null,
        },
      });

      if (response.data && response.data.status) {
        const batchesData = Array.isArray(response.data.data) ? response.data.data : [];
        setBatches(batchesData);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalPages(response.data.totalPages || 0);
        
        if (!search.trim()) {
          setOriginalBatchesCount(batchesData.length);
        }
      } else {
        setBatches([]);
        setTotalRecords(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
      showErrorToast("Failed to load batches");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [CompID, search, pageSize, pageNumber, sortBy, sortOrder, batchStatus, isCompleted]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchBatches();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchBatches]);

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
  };

  const handleOpenAddPage = () => {
    navigate("/batches/createBatch");
  };

  const handleOpenEditPage = (batch) => {
    navigate(`/batches/updateBatch/${batch.batchId}`);
  };

  const handleDelete = async (batch) => {
    const result = await swal({
      title: "Are you sure?",
      text: `Do you want to delete batch "${batch.batchNumber}"?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (result) {
      try {
        setLoading(true);
        const response = await axiosInstance.delete(APIs.DELETE_RECIPE_BATCH, {
          params: {
            BatchId: batch.batchId,
            CompID: CompID,
          },
        });

        if (response.data && response.data.status) {
          showSuccessToast("Batch deleted successfully");
          fetchBatches();
        } else {
          showErrorToast(response.data?.message || "Failed to delete batch");
        }
      } catch (error) {
        console.error("Error deleting batch:", error);
        showErrorToast(error.response?.data?.message || "Failed to delete batch");
      } finally {
        setLoading(false);
      }
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-success";
      case "inprogress":
        return "bg-warning";
      case "draft":
        return "bg-secondary";
      default:
        return "bg-secondary";
    }
  };

  const clearFilters = () => {
    setSearch("");
    setBatchStatus(null);
    setIsCompleted(null);
    setPageNumber(1);
  };

  // Check if any filter is applied
  const hasFiltersApplied = () => {
    return (
      search.trim() !== "" ||
      batchStatus !== null ||
      isCompleted !== null
    );
  };

  const renderNoDataMessage = () => {
    if (batches.length === 0 && hasFiltersApplied()) {
      return (
        <div className="alert alert-info text-center mt-3">
          <i className="fa fa-info-circle me-2"></i>
          No batches found matching your filter criteria.
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Batch</li>
          </ol>
          <h1 className="page-header mb-0">Manage Batch</h1>
        </div>
        <div className="ms-auto">
          <div className="d-flex justify-content-end align-items-center mb-3 gap-2">
            {!isChildBranch && (
              <button
                className="btn px-4"
                onClick={handleOpenAddPage}
                style={{
                  backgroundColor: themeColor,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  border: 'none',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: `0 2px 8px ${themeColor}40`
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                }}
              >
                <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
                Add Batch
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {/* Show centered Add Batch button only when no batches exist and no filters are applied */}
        {!isChildBranch && !loading && batches.length === 0 && !hasFiltersApplied() && originalBatchesCount === 0 && (
          <AddButton onClick={handleOpenAddPage} title="Add Batch" />
        )}

        {/* Show filter bar when: batches exist, filters are applied, or we've loaded data before */}
        {(!loading && (batches.length > 0 || hasFiltersApplied() || originalBatchesCount > 0)) && (
          <>
            <div className="card border-0">
              <div className="tab-content p-3">
                <div className="tab-pane fade show active" id="allTab">
                  {/* Enhanced Filter Section */}
                  <div className="mb-4">
                    <div
                      className="card filter-card"
                      style={{
                        position: "relative",
                        zIndex: 1,
                        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: `0 4px 20px ${themeColor}40`
                      }}
                    >
                      <div className="card-body p-3">
                        <div className="row g-3 align-items-center">
                          {/* Search Field */}
                          <div className="col-xl-3 col-lg-3 col-md-6">
                            <div className="search-input filter-control">
                              <input
                                type="text"
                                className="filter-input"
                                value={search}
                                onChange={(e) => {
                                  setSearch(e.target.value);
                                  setPageNumber(1);
                                }}
                                placeholder="Search batches..."
                                style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  border: '1px solid rgba(255, 255, 255, 0.3)',
                                  color: '#333'
                                }}
                              />
                            </div>
                          </div>

                          {/* Batch Status Dropdown */}
                          <div className="col-xl-2 col-lg-2 col-md-6">
                            <div className="filter-control">
                              <Select
                                options={batchStatusOptions}
                                value={batchStatus}
                                onChange={(v) => {
                                  setBatchStatus(v);
                                  setPageNumber(1);
                                }}
                                styles={dropdownStyles}
                                placeholder="Batch Status"
                                className="filter-dropdown"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuShouldBlockScroll={false}
                                menuShouldScrollIntoView={false}
                                closeMenuOnScroll={false}
                                isClearable
                              />
                            </div>
                          </div>

                          {/* Completion Status Dropdown */}
                          <div className="col-xl-2 col-lg-2 col-md-6">
                            <div className="filter-control">
                              <Select
                                options={isCompletedOptions}
                                value={isCompleted}
                                onChange={(v) => {
                                  setIsCompleted(v);
                                  setPageNumber(1);
                                }}
                                styles={dropdownStyles}
                                placeholder="Completion"
                                className="filter-dropdown"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuShouldBlockScroll={false}
                                menuShouldScrollIntoView={false}
                                closeMenuOnScroll={false}
                                isClearable
                              />
                            </div>
                          </div>

                          {/* Order By Dropdown */}
                          <div className="col-xl-2 col-lg-2 col-md-6">
                            <div className="filter-control">
                              <Select
                                value={sortBy}
                                onChange={setSortBy}
                                options={sortByOptions}
                                styles={dropdownStyles}
                                placeholder="Order By"
                                className="filter-dropdown"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuShouldBlockScroll={false}
                                menuShouldScrollIntoView={false}
                                closeMenuOnScroll={false}
                              />
                            </div>
                          </div>

                          {/* Sort Order Dropdown */}
                          <div className="col-xl-1 col-lg-1 col-md-6">
                            <div className="filter-control">
                              <SortOrderDropDownSelection
                                defaultValue={sortOrder}
                                onChange={setSortOrder}
                              />
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="col-xl-2 col-lg-2 col-md-12">
                            <div className="d-flex gap-2">
                              <button
                                className="clear-btn"
                                onClick={clearFilters}
                                title="Clear all filters"
                              >
                                <i className="fa fa-times me-1"></i>
                                Filter
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {renderNoDataMessage()}

                  {Array.isArray(batches) && batches.length > 0 && (
                    <>
                      <div
                        className="table-responsive table-container"
                        style={{
                          minHeight: "200px",
                          maxHeight: "450px",
                          overflowY: "auto",
                          overflowX: "auto",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <table className="table table-hover" style={{ position: "relative" }}>
                          <thead
                            className="bg-light sticky-header"
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 10000,
                              backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                              color: isDarkMode ? "#ffffff" : "#000000",
                            }}
                          >
                            <tr>
                              <th className="pt-2 pb-2">Batch ID</th>
                              <th className="pt-2 pb-2">Batch Number</th>
                              <th className="pt-2 pb-2">Product</th>
                              <th className="pt-2 pb-2 text-center">Quantity</th>
                              <th className="pt-2 pb-2">Unit</th>
                              <th className="pt-2 pb-2 text-center">Status</th>
                              <th className="pt-2 pb-2 text-center">Cost Price</th>
                              <th className="pt-2 pb-2">Created Date</th>
                              <th className="pt-2 pb-2 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {batches.map((batch) => {
                              const menuItem = menuItems.find(item => item.value === batch.menuItemId);
                              return (
                                <tr key={batch.batchId}>
                                  <td className="align-middle">
                                    {batch.batchId}
                                  </td>
                                  <td className="align-middle">
                                    {batch.batchNumber || "N/A"}
                                  </td>
                                  <td className="align-middle">
                                    {menuItem?.label || `Item #${batch.menuItemId}`}
                                  </td>
                                  <td className="align-middle text-center">
                                    {batch.quantity || "0"}
                                  </td>
                                  <td className="align-middle">
                                    {batch.unit || "N/A"}
                                  </td>
                                  <td className="align-middle text-center">
                                    <span className={`badge ${getStatusBadgeClass(batch.batchStatus)}`}>
                                      {batch.batchStatus || "Draft"}
                                    </span>
                                  </td>
                                  <td className="align-middle text-center">
                                    {batch.costPrice ? `$${parseFloat(batch.costPrice).toFixed(2)}` : "$0.00"}
                                  </td>
                                  <td className="align-middle">
                                    {batch.createdDate ? new Date(batch.createdDate).toLocaleDateString() : "N/A"}
                                  </td>
                                  <td className="align-middle text-center">
                                    <div className="d-flex justify-content-center gap-2">
                                      {/* Edit Icon */}
                                      {!isChildBranch && (
                                        <button
                                          onClick={() => handleOpenEditPage(batch)}
                                          className="btn btn-sm btn-outline-primary"
                                          title="Edit Batch"
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '6px',
                                            border: `1px solid ${themeColor}`,
                                            color: themeColor,
                                            transition: 'all 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = themeColor;
                                            e.currentTarget.style.color = 'white';
                                            e.currentTarget.style.transform = 'scale(1.05)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = themeColor;
                                            e.currentTarget.style.transform = 'scale(1)';
                                          }}
                                        >
                                          <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                        </button>
                                      )}

                                      {/* Delete Icon */}
                                      <button
                                        onClick={() => handleDelete(batch)}
                                        className="btn btn-sm btn-outline-danger"
                                        title="Delete Batch"
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '6px',
                                          border: '1px solid #dc3545',
                                          color: '#dc3545',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#dc3545';
                                          e.currentTarget.style.color = 'white';
                                          e.currentTarget.style.transform = 'scale(1.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                          e.currentTarget.style.color = '#dc3545';
                                          e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                      >
                                        <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
                <Pagination
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                  setPageSize={setPageSize}
                  themeColor={themeColor}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ManageBatch;
